import styled from '@emotion/styled';
import { CSSProperties, PropsWithChildren, Ref } from 'react';
import AdSlot from '~/modules/Ads/AdSlot';

interface AdSupportedLayoutProps extends PropsWithChildren {
  placementPrefix: string;
  variant?: 'page' | 'stage';
  contentScroll?: boolean;
  contentRef?: Ref<HTMLDivElement>;
  contentStyle?: CSSProperties;
}

export default function AdSupportedLayout({
  children,
  placementPrefix,
  variant = 'page',
  contentScroll = false,
  contentRef,
  contentStyle,
}: AdSupportedLayoutProps) {
  return (
    <Shell data-variant={variant} data-content-scroll={contentScroll}>
      <Frame data-variant={variant} data-content-scroll={contentScroll}>
        <MainColumn data-variant={variant} data-content-scroll={contentScroll}>
          <TopPanel>
            <AdSlot placement={`${placementPrefix}-top-banner`} minHeight="clamp(56px, 18vw, 96px)" />
          </TopPanel>

          <Content data-variant={variant} data-content-scroll={contentScroll} ref={contentRef} style={contentStyle}>
            {children}
          </Content>
        </MainColumn>

        <AdRail aria-label="Side rail" data-variant={variant} data-content-scroll={contentScroll}>
          <ChatbotSlot data-chatbot-slot={placementPrefix} aria-label="Chatbot">
            Chatbot
          </ChatbotSlot>
          <SideAdSlot>
            <AdSlot placement={`${placementPrefix}-left-skyscraper`} minHeight="clamp(96px, 28vw, 420px)" />
          </SideAdSlot>
        </AdRail>
      </Frame>
    </Shell>
  );
}

const Shell = styled.div`
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  min-height: 100%;
  color: var(--walky-ink);
  overflow-x: hidden;

  &[data-content-scroll='true'] {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  &[data-variant='stage'] {
    min-height: 100vh;
    min-height: 100dvh;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  @media (max-width: 920px) {
    &[data-content-scroll='true'] {
      overflow-y: auto;
    }
  }
`;

const Frame = styled.div`
  width: min(100%, 1480px);
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;

  &[data-content-scroll='true'] {
    height: 100%;
    min-height: 0;
    align-items: stretch;
  }

  &[data-variant='stage'] {
    height: 100%;
    box-sizing: border-box;
    padding: 12px;
  }

  @media (max-width: 920px) {
    grid-template-columns: 1fr;

    &[data-content-scroll='true'] {
      display: flex;
      flex-direction: column;
      height: auto;
      min-height: 100%;
      align-items: start;
    }

    &[data-variant='stage'] {
      overflow-y: auto;
    }
  }
`;

const AdRail = styled.aside`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 12px;
  min-width: 0;

  &[data-content-scroll='true'] {
    position: static;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  &[data-variant='stage'] {
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
  }

  @media (max-width: 920px) {
    grid-column: 1;
    grid-row: auto;
    position: static;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    &[data-content-scroll='true'] {
      order: 2;
      width: 100%;
    }

    &[data-variant='stage'] {
      max-height: none;
      overflow-y: visible;
    }

    &[data-content-scroll='true'] {
      height: auto;
      overflow: visible;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.section`
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  &[data-content-scroll='true'] {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &[data-variant='stage'] {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  @media (max-width: 920px) {
    grid-column: 1;
    grid-row: auto;

    &[data-content-scroll='true'] {
      order: 1;
      width: 100%;
      height: auto;
      overflow: visible;
    }
  }
`;

const TopPanel = styled.div`
  flex: 0 0 auto;
  display: block;
  margin-bottom: 16px;
`;

const SideAdSlot = styled.div`
  min-width: 0;
`;

const ChatbotSlot = styled.div`
  flex: 1 1 420px;
  min-height: 420px;
  border: 1px solid var(--walky-border);
  border-top: 3px solid var(--walky-orange);
  border-radius: 8px;
  background: var(--walky-surface);
  color: var(--walky-ink);
  box-shadow: var(--walky-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;

  @media (max-width: 920px) {
    order: 2;
    min-height: clamp(220px, 64vw, 320px);
  }
`;

const Content = styled.div`
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &[data-content-scroll='true'] {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
  }

  &[data-variant='stage'] {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  @media (max-width: 920px) {
    &[data-content-scroll='true'] {
      overflow: visible;
    }
  }
`;
